import React, { useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import { t } from '@lingui/core/macro'
import OrderInfo from '../../components/OrderInfo/OrderInfo'
import TimeLeft from '../../components/TimeLeft/TimeLeft'
import Expired from '../../components/Expired/Expired'
import MainButton from '../../components/MainButton/MainButton'
import { P2PPayPageData } from '../../loaders/p2pPayPageLoader'
import { buildBankDeepLink } from '../../utils/bankDeepLinks'
import { payP2PConfirm } from '../../api/payP2PConfirm'
import { Bank, getBankNames } from '../../types/Bank'
import { getCountryNames } from '../../types/Country'
import styles from './P2PPage.module.css'

const P2PPage = () => {
    const { paymentPageInfo, transaction } = useLoaderData<P2PPayPageData>()
    const navigate = useNavigate()

    const [isExpired, setIsExpired] = useState(
        paymentPageInfo.expiresAt && new Date(paymentPageInfo.expiresAt) < new Date()
    )

    const payerBank = transaction.p2p.bank ? transaction.p2p.bank as Bank : null
    const payerBankName = getBankNames().find((bankName) => bankName.bank === payerBank)?.name || t`Неизвестный банк`

    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    const isAndroid = /Android/i.test(navigator.userAgent)
    const isMobile = isIOS || isAndroid

    const deepLink = (payerBank && isMobile) ? buildBankDeepLink(
        payerBank,
        isIOS ? 'ios' : 'android',
        {
            requisiteType: transaction.requisite.type,
            number: transaction.requisite.number,
            amount: transaction.requisite.amount / 100,
            country: transaction.requisite.country,
            bank: transaction.requisite.bank as Bank,
        }
    ) : null

    const handleDeepLink = () => {
        if (!deepLink) {
            return
        }

        // На iOS Universal Link корректнее открывать через location.href,
        // window.open в новой вкладке Safari иногда блокирует переход в приложение.
        window.open(deepLink, '_blank')
        // window.location.href = deepLink
    }

    const handlePaymentComplete = async () => {
        await payP2PConfirm(paymentPageInfo.id, transaction.transactionId)

        navigate(`/result/${paymentPageInfo.id}/${transaction.transactionId}`)
    }

    const formatCardNumber = (cardNumber: string) => {
        return cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ')
    }

    const formatPhoneNumber = (phoneNumber: string) => {
        const formatedPhoneNumber = `+${phoneNumber}`

        try {
            const parsed = parsePhoneNumberWithError(formatedPhoneNumber)

            if (parsed?.isValid()) {
                return parsed.formatInternational() // '+7 912 345-67-89'
            }

            return formatedPhoneNumber
        } catch {
            return formatedPhoneNumber
        }
    }

    if (isExpired) {
        return (
            <Expired
                orderId={paymentPageInfo.orderId}
                redirectUrl={paymentPageInfo.redirectUrl}
                redirectTimeout={paymentPageInfo.redirectTimeout}
            />
        )
    }

    return (
        <>
            <TimeLeft
                expiresAt={paymentPageInfo.expiresAt}
                onExpired={() => setIsExpired(true)}
            />
            <div className={styles.container}>
                <h2 className={styles.title}>{t`P2P перевод`}</h2>

                <p className={styles.text}>
                    {t`Переведите указанную сумму на следующие реквизиты в приложении вашего банка`}
                </p>

                <OrderInfo transaction={transaction} amount={transaction.requisite.amount} />

                <div className={styles.transferSection}>
                    <h3 className={styles.sectionTitle}>{t`Реквизиты для перевода`}</h3>

                    <div className={styles.requisites}>
                        {transaction.requisite.type === 'card' && (
                            <div className={styles.requisiteItem}>
                                <span className={styles.requisiteLabel}>{t`Номер карты`}</span>
                                <span className={styles.requisiteValue}>
                                    {formatCardNumber(transaction.requisite.number)}
                                </span>
                            </div>
                        )}

                        {transaction.requisite.type === 'sbp' && (
                            <div className={styles.requisiteItem}>
                                <span className={styles.requisiteLabel}>{ t`Номер телефона` }</span>
                                <span className={styles.requisiteValue}>
                                    {formatPhoneNumber(transaction.requisite.number)}
                                </span>
                            </div>
                        )}

                        <div className={styles.requisiteItem}>
                            <span className={styles.requisiteLabel}>{t`Банк получателя`}</span>
                            <span className={styles.requisiteValue}>{getBankNames().find(bankName => bankName.bank === transaction.requisite.bank)?.name ?? transaction.requisite.bank}</span>
                        </div>

                        {transaction.requisite.country !== 'RU' && (
                            <div className={styles.requisiteItem}>
                                <span className={styles.requisiteLabel}>{t`Страна`}</span>
                                <span className={styles.requisiteValue}>{getCountryNames().find(countryName => countryName.country === transaction.requisite.country)?.name ?? transaction.requisite.country}</span>
                            </div>
                        )}

                        {transaction.requisite.description && (
                            <div className={styles.requisiteItem}>
                                <span className={styles.requisiteLabel}>{t`Описание`}</span>
                                <span className={styles.requisiteValue}>
                                    {transaction.requisite.description}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.infoBlock}>
                    <div className={styles.infoItem}>
                        <strong>{t`Важно:`}</strong> {t`Сумма перевода должна быть в точности такой же, как указана в реквизитах для перевода. Она может немного отличаться от изначальной суммы заказа. Это необходимо, чтобы быстро зачислить перевод в автоматическом режиме.`}
                    </div>
                    <div className={styles.infoItem}>
                        <strong>{t`Внимание:`}</strong> {t`При переводе средств категорически запрещено указывать любые комментарии!`}
                    </div>
                </div>

                {isMobile && deepLink && (
                    <div className={styles.deepLinkSection}>
                        <button type="button" className={styles.deepLinkButton} onClick={handleDeepLink}>
                            <span className={styles.deepLinkIcon}>🏦</span>
                            <span className={styles.deepLinkText}>
                                {t`Открыть в приложении ${payerBankName}`}
                            </span>
                        </button>
                    </div>
                )}

                <div className={styles.confirmSection}>
                    <MainButton
                        message={t`Я оплатил`}
                        type="button"
                        onClick={handlePaymentComplete}
                        disabled={false}
                    />
                </div>
            </div>
        </>
    )
}

export default P2PPage
